import React from 'react';
import { TextField, Radio, Checkbox, ToggleButton, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';

import type { Questionary } from '@shared/interfaces/assistantScripts';
import { ScriptToggleButtonGroup } from '../AssistantScriptDialog.styled';
import {
  MultipleChoiceContainer,
  OptionRow,
  OptionLabelCircle,
  DeleteOptionButton,
  AddOptionBox,
} from './styled';

type MultipleChoiceQuestionary = Extract<
  Questionary,
  { type: 'multiple_choice' }
>;

const MAX_OPTIONS = 6;
const LABELS = ['A', 'B', 'C', 'D', 'E', 'F'];

interface MultipleChoiceEditorProps {
  questionary: MultipleChoiceQuestionary;
  index: number;
  onUpdate: (q: MultipleChoiceQuestionary) => void;
}

const MultipleChoiceEditor = ({
  questionary: q,
  index,
  onUpdate,
}: MultipleChoiceEditorProps) => {
  const { t } = useTranslation('common');

  return (
    <MultipleChoiceContainer>
      <ScriptToggleButtonGroup
        value={q.selectionMode}
        exclusive
        onChange={(_, value) => {
          if (value === null) {
            return;
          }

          onUpdate({ ...q, selectionMode: value, correctAnswers: [] });
        }}
        size="small"
      >
        <ToggleButton
          value="single"
          data-testid={`script-step-${index}-answer-mode-single`}
        >
          {t('assistant.scriptDialog.singleAnswer')}
        </ToggleButton>
        <ToggleButton
          value="multiple"
          data-testid={`script-step-${index}-answer-mode-multiple`}
        >
          {t('assistant.scriptDialog.multipleAnswers')}
        </ToggleButton>
      </ScriptToggleButtonGroup>

      {q.options.map((option, optIdx) => (
        <OptionRow key={optIdx}>
          <OptionLabelCircle>{LABELS[optIdx]}</OptionLabelCircle>
          <TextField
            fullWidth
            size="small"
            placeholder={`Option ${LABELS[optIdx]}`}
            value={option}
            onChange={(e) => {
              const newOptions = [...q.options];
              newOptions[optIdx] = e.target.value;
              onUpdate({ ...q, options: newOptions });
            }}
            inputProps={{
              'data-testid': `script-step-${index}-option-${optIdx}-input`,
            }}
          />
          {q.selectionMode === 'single' ? (
            <Radio
              size="small"
              checked={q.correctAnswers[0] === optIdx}
              onChange={() => onUpdate({ ...q, correctAnswers: [optIdx] })}
              sx={{ flexShrink: 0, p: 0.5 }}
              data-testid={`script-step-${index}-option-${optIdx}-correct`}
            />
          ) : (
            <Checkbox
              size="small"
              checked={q.correctAnswers.includes(optIdx)}
              onChange={(e) => {
                const newCorrect = e.target.checked
                  ? [...q.correctAnswers, optIdx]
                  : q.correctAnswers.filter((i) => i !== optIdx);
                onUpdate({ ...q, correctAnswers: newCorrect });
              }}
              sx={{ flexShrink: 0, p: 0.5 }}
              data-testid={`script-step-${index}-option-${optIdx}-correct`}
            />
          )}
          {q.options.length > 2 && (
            <DeleteOptionButton
              size="small"
              onClick={() => {
                const newOptions = q.options.filter((_, i) => i !== optIdx);
                const newCorrect = q.correctAnswers
                  .filter((i) => i !== optIdx)
                  .map((i) => (i > optIdx ? i - 1 : i));
                onUpdate({
                  ...q,
                  options: newOptions,
                  correctAnswers: newCorrect,
                });
              }}
              data-testid={`script-step-${index}-option-${optIdx}-delete-btn`}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </DeleteOptionButton>
          )}
        </OptionRow>
      ))}

      {q.options.length < MAX_OPTIONS && (
        <AddOptionBox
          onClick={() => onUpdate({ ...q, options: [...q.options, ''] })}
          data-testid={`script-step-${index}-add-option-btn`}
        >
          {t('assistant.scriptDialog.addOption', {
            count: q.options.length,
            max: MAX_OPTIONS,
          })}
        </AddOptionBox>
      )}
    </MultipleChoiceContainer>
  );
};

export default MultipleChoiceEditor;
